import { TAU, WHITE, circle, glow, rim } from '../paint';
import type { Ctx, Piece } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';

export const UNIT = 2;

export const HAIR = 0x1a120c;
export const VISOR = 0x0a0d22;

const TORSO = { width: 8.5, height: 13.5 } as const;
export const GARLAND = { lights: 14, from: -7, to: 7, wave: 10, turns: 1.5, light: 1.2 } as const;
export const STICKS = { along: 11, across: 11, turn: 0.45, middle: 5, length: 12, width: 2.6 };
export const HANDS = { along: 10, across: 11 } as const;
export const HEAD = { along: 2, hair: 1 } as const;
export const GLASSES = { along: 8.1, length: 11, width: 2.6 } as const;

function units(draw: (ctx: Ctx) => void): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.save();
    ctx.scale(UNIT, UNIT);
    draw(ctx);
    ctx.restore();
  };
}

export function garlandLight(index: number): { along: number; across: number } {
  const share = index / (GARLAND.lights - 1);
  return {
    along: GARLAND.from + (GARLAND.to - GARLAND.from) * share,
    across: Math.sin(share * TAU * GARLAND.turns) * GARLAND.wave,
  };
}

function torsoPath(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, TORSO.width, TORSO.height, 0, 0, TAU);
}

function torso(ctx: Ctx): void {
  torsoPath(ctx);
  ctx.fillStyle = '#e4e4e4';
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(1.5, 0, TORSO.width / 2, TORSO.height * 0.66, 0, 0, TAU);
  ctx.fillStyle = WHITE;
  ctx.fill();
  torsoPath(ctx);
  rim(ctx, 2);
}

function bulbs(ctx: Ctx): void {
  for (let index = 0; index < GARLAND.lights; index += 1) {
    const { along, across } = garlandLight(index);
    circle(ctx, GARLAND.light, along, across);
    ctx.fillStyle = WHITE;
    ctx.fill();
  }
}

function stick(ctx: Ctx): void {
  const half = STICKS.length / 2;
  ctx.beginPath();
  ctx.moveTo(-half, 0);
  ctx.lineTo(half, 0);
  ctx.lineCap = 'round';
  glow(ctx, WHITE, 1.5);
  ctx.lineWidth = STICKS.width;
  ctx.strokeStyle = WHITE;
  ctx.stroke();
}

function hand(ctx: Ctx): void {
  circle(ctx, 3.2);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 1.3);
}

function head(ctx: Ctx): void {
  circle(ctx, 6.8);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 1.6);
}

function hair(ctx: Ctx): void {
  circle(ctx, 5.4);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

function glassesPath(ctx: Ctx): void {
  ctx.beginPath();
  ctx.roundRect(
    -GLASSES.width / 2,
    -GLASSES.length / 2,
    GLASSES.width,
    GLASSES.length,
    GLASSES.width / 2,
  );
}

function glasses(ctx: Ctx): void {
  glassesPath(ctx);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

function leds(ctx: Ctx): void {
  glassesPath(ctx);
  ctx.lineWidth = 1.1;
  ctx.strokeStyle = WHITE;
  ctx.stroke();
}

function soft(width: number, height: number): Piece {
  return [
    96,
    96,
    REFERENCE_RADIUS,
    (ctx) => {
      glow(ctx, WHITE, 8);
      ctx.fillStyle = WHITE;
      ctx.beginPath();
      ctx.ellipse(0, 0, width, height, 0, 0, TAU);
      ctx.fill();
    },
  ];
}

export const SUPERFAN_PIECES = {
  torso: [40, 60, REFERENCE_RADIUS, units(torso)],
  bulbs: [36, 48, REFERENCE_RADIUS, units(bulbs)],
  stick: [36, 14, REFERENCE_RADIUS, units(stick)],
  hand: [18, 18, REFERENCE_RADIUS, units(hand)],
  head: [34, 34, REFERENCE_RADIUS, units(head)],
  hair: [24, 24, REFERENCE_RADIUS, units(hair)],
  glasses: [12, 28, REFERENCE_RADIUS, units(glasses)],
  leds: [12, 28, REFERENCE_RADIUS, units(leds)],
  shadow: soft(16, 26),
} satisfies Record<string, Piece>;

const THROWN = { length: 26, width: 5.5 } as const;

function thrown(ctx: Ctx): void {
  const half = THROWN.length / 2;
  ctx.beginPath();
  ctx.moveTo(-half, 0);
  ctx.lineTo(half, 0);
  ctx.lineCap = 'round';
  glow(ctx, WHITE, 3);
  ctx.lineWidth = THROWN.width;
  ctx.strokeStyle = WHITE;
  ctx.stroke();
}

function thrownShadow(ctx: Ctx): void {
  const half = THROWN.length / 2;
  ctx.beginPath();
  ctx.moveTo(-half, 0);
  ctx.lineTo(half, 0);
  ctx.lineCap = 'round';
  ctx.lineWidth = THROWN.width;
  ctx.strokeStyle = WHITE;
  ctx.stroke();
}

export const GLOWSTICK_PIECES = {
  stick: [44, 16, REFERENCE_RADIUS, thrown],
  stickShadow: [40, 12, REFERENCE_RADIUS, thrownShadow],
} satisfies Record<string, Piece>;
